// ===================================
// SIMPLIFIED TOPIC DETAIL WITHOUT MODAL CONFLICTS - src/components/TopicDetail.tsx
// ===================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import TopicsApi from '../services/topicsApi';
import { parseUserPermissions, canUserModifyTopic, canUserDeleteTopic } from '../utils/permissions';
import { Topic, Action, PaginatedResponse } from '../types';
import { 
  X, 
  Edit, 
  Trash2, 
  Calendar, 
  Building2, 
  Hash,
  FileText,
  Activity,
  ChevronRight,
  Zap
} from 'lucide-react';
import Button from './ui/button';
import Badge from './ui/badge';

interface TopicDetailProps {
  isOpen: boolean;
  topic: Topic | null;
  onClose: () => void;
  onEdit: (topic: Topic) => void;
  onDelete: (topic: Topic) => void;
}

const TopicDetail: React.FC<TopicDetailProps> = ({
  isOpen,
  topic,
  onClose,
  onEdit,
  onDelete
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const permissions = parseUserPermissions(user);

  // State for actions preview
  const [actions, setActions] = useState<Action[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsError, setActionsError] = useState<string | null>(null);

  // Load actions preview for the topic (just first few)
  const loadActionsPreview = async () => {
    if (!topic) return;

    try {
      setActionsLoading(true);
      setActionsError(null);

      const response: PaginatedResponse<Action> = await TopicsApi.getTopicActions({
        topic_id: topic.id,
        limit: 5 // Show first 5 actions as preview
      });

      setActions(response.items);

    } catch (err) {
      const error = TopicsApi.handleApiError(err);
      setActionsError(error.message);
      console.error('Failed to load topic actions preview:', error);
    } finally {
      setActionsLoading(false);
    }
  };

  // Load actions when topic changes
  useEffect(() => {
    if (isOpen && topic) {
      loadActionsPreview();
    }
  }, [isOpen, topic]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Handle delete with confirmation
  const handleDelete = () => {
    if (!topic) return;

    if (window.confirm(`Are you sure you want to delete the topic "${topic.name}"? This will also delete all ${topic.action_count} actions in this topic.`)) {
      onDelete(topic);
    }
  };

  // Navigate to full actions management page
  const handleManageActions = () => {
    if (!topic) return;
    onClose(); // Close this modal first
    navigate(`/topics/${topic.id}/actions`);
  };

  if (!isOpen || !topic) return null;

  const canModify = canUserModifyTopic(user, topic);
  const canDelete = canUserDeleteTopic(user, topic);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">{topic.name}</h2>
              <Badge variant="secondary">
                {topic.action_count} action{topic.action_count !== 1 ? 's' : ''}
              </Badge>
            </div>
            {topic.description && (
              <p className="text-gray-600 mt-1">{topic.description}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {canModify && (
              <Button variant="outline" size="sm" onClick={() => onEdit(topic)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Topic
              </Button>
            )}
            
            {canDelete && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDelete}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
            
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Topic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Topic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">ID:</span>
                <span className="font-medium">{topic.id}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Business:</span>
                <span className="font-medium">
                  {topic.business_id ? `ID ${topic.business_id}` : 'Global'}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Updated:</span>
                <span className="font-medium">
                  {new Date(topic.updated_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Actions Preview */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Actions Preview
              </h3>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleManageActions}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <Zap className="h-4 w-4 mr-2" />
                Manage All Actions
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            {actionsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : actionsError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{actionsError}</p>
              </div>
            ) : actions.length > 0 ? (
              <div className="space-y-3">
                {actions.map((action) => (
                  <div
                    key={action.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Zap className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {action.action_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        Flow: {action.flow_name}
                      </p>
                    </div>
                    <div className="text-xs text-gray-500">
                      {action.required_fields?.length || 0} required fields
                    </div>
                  </div>
                ))}
                
                {topic.action_count > 5 && (
                  <div className="text-center py-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleManageActions}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      View {topic.action_count - 5} more actions
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Activity className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-3">No actions in this topic yet</p>
                {permissions.canCreate && (
                  <Button 
                    size="sm" 
                    onClick={handleManageActions}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Create First Action
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Quick Stats</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Total Actions:</span>
                <span className="font-medium text-gray-900 ml-2">{topic.action_count}</span>
              </div>
              <div>
                <span className="text-gray-600">Created:</span>
                <span className="font-medium text-gray-900 ml-2">
                  {new Date(topic.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopicDetail;